import { getSmallTextBoxTemplate } from "../../shortcodes/templates";
import { addShortcodeToEditor, getShortcodeWrapper } from "@/assets/utilities/tinymce/shortodeAddition";
import { parseToHtml } from "@/utility/parseToHtml";

const DATA_ATTR = "data-small-text-box-data";
const TEXT_REQUIRED_ERROR = "Please enter the text.";
const FONT_SIZE_REQUIRED_ERROR = "Please enter a font size.";
const FONT_SIZE_INVALID_ERROR = "Enter a number greater than 0 (e.g. 12).";
const BORDER_SIZE_REQUIRED_ERROR = "Please enter a border size.";
const BORDER_SIZE_INVALID_ERROR = "Enter a number of 0 or more (e.g. 1.5).";

const defaultData = {
  text: "BENEFIT #1",
  textColor: "#2454C7",
  borderColor: "#2454C7",
  bgColor: "#FFFFFF",
  fontSize: "12",
  borderWidth: "1.5",
  alignment: "left",
};

const encodeData = data => btoa(unescape(encodeURIComponent(JSON.stringify(data))));
const decodeData = encoded => JSON.parse(decodeURIComponent(escape(window.atob(encoded))));

const isValidFontSize = value => /^\d+(\.\d+)?$/.test(value) && Number(value) > 0;
const isValidBorderWidth = value => /^\d+(\.\d+)?$/.test(value) && Number(value) >= 0;

const normalizePxValue = value => {
  const trimmed = String(value ?? "")
    .trim()
    .replace(/px$/i, "");
  return trimmed;
};

const normalizeData = data => ({
  ...data,
  text: data.text?.trim() ?? "",
  fontSize: normalizePxValue(data.fontSize),
  borderWidth: normalizePxValue(data.borderWidth),
});

const buildBodyItems = errors => {
  const items = [
    {
      type: "input",
      name: "text",
      label: "Text",
      placeholder: "BENEFIT #1",
    },
    {
      type: "colorinput",
      name: "textColor",
      label: "Text color",
    },
    {
      type: "colorinput",
      name: "borderColor",
      label: "Box outline color",
    },
    {
      type: "colorinput",
      name: "bgColor",
      label: "Background color",
    },
    {
      type: "grid",
      columns: 2,
      items: [
        {
          type: "input",
          name: "fontSize",
          label: "Font size (px)",
          placeholder: "12",
          inputMode: "numeric",
        },
        {
          type: "input",
          name: "borderWidth",
          label: "Border size (px)",
          placeholder: "1.5",
          inputMode: "numeric",
        },
      ],
    },
    {
      type: "selectbox",
      name: "alignment",
      label: "Alignment",
      items: [
        { value: "left", text: "Left" },
        { value: "center", text: "Center" },
        { value: "right", text: "Right" },
      ],
    },
  ];

  const errorPanel = message => ({
    type: "htmlpanel",
    html: `<div style="color:#c0392b;font-size:12px;margin-top:-12px;">${message}</div>`,
  });

  if (errors?.text) {
    const index = items.findIndex(item => item.name === "text");
    items.splice(index + 1, 0, errorPanel(errors.text));
  }

  // fontSize and borderWidth share a 2-column grid row — surface their errors below it.
  const gridIndex = items.findIndex(item => item.type === "grid");
  [errors?.fontSize, errors?.borderWidth].filter(Boolean).forEach((message, offset) => {
    items.splice(gridIndex + 1 + offset, 0, errorPanel(message));
  });

  return items;
};

const buildDialogConfig = ({ submitText, initialData, errors, onValid }) => ({
  title: "Small Text Box",
  body: {
    type: "panel",
    items: buildBodyItems(errors),
  },
  initialData,
  buttons: [
    {
      type: "cancel",
      name: "close",
      text: "Cancel",
    },
    {
      type: "submit",
      name: "submit",
      text: submitText,
      primary: true,
    },
  ],
  onSubmit: api => {
    const data = normalizeData(api.getData());
    const nextErrors = {};

    if (!data.text) nextErrors.text = TEXT_REQUIRED_ERROR;
    if (!data.fontSize) nextErrors.fontSize = FONT_SIZE_REQUIRED_ERROR;
    else if (!isValidFontSize(data.fontSize)) nextErrors.fontSize = FONT_SIZE_INVALID_ERROR;
    if (!data.borderWidth) nextErrors.borderWidth = BORDER_SIZE_REQUIRED_ERROR;
    else if (!isValidBorderWidth(data.borderWidth)) nextErrors.borderWidth = BORDER_SIZE_INVALID_ERROR;

    if (Object.keys(nextErrors).length) {
      api.redial(buildDialogConfig({ submitText, initialData: data, errors: nextErrors, onValid }));
      api.focus(["text", "borderWidth", "fontSize"].find(field => nextErrors[field]));
      return;
    }

    onValid(api, data);
  },
});

const smallTextBoxConfig = (tinymce, uuid, shortcodeOnClick) => {
  tinymce.activeEditor.windowManager.open(
    buildDialogConfig({
      submitText: "Add",
      initialData: defaultData,
      onValid: (api, data) => {
        const parentElement = tinymce.activeEditor.selection.getNode().nodeName.toLowerCase();

        const template = getSmallTextBoxTemplate(data);
        const wrapperEl = parseToHtml(getShortcodeWrapper(uuid, shortcodeOnClick, template)).querySelector(
          ".shortcode",
        );
        wrapperEl.setAttribute(DATA_ATTR, encodeData(data));

        addShortcodeToEditor(wrapperEl.outerHTML, uuid, parentElement, api, false);
        api.close();
      },
    }),
  );
};

const editSmallTextBoxConfig = (editor, shortcodeWrapper) => {
  const encoded = shortcodeWrapper.getAttribute(DATA_ATTR);
  const initialData = normalizeData(encoded ? { ...defaultData, ...decodeData(encoded) } : defaultData);

  editor.windowManager.open(
    buildDialogConfig({
      submitText: "Save",
      initialData,
      onValid: (api, data) => {
        shortcodeWrapper.setAttribute(DATA_ATTR, encodeData(data));
        const box = shortcodeWrapper.querySelector(".small-text-box");
        if (box) {
          box.outerHTML = getSmallTextBoxTemplate(data);
        }
        // Persist the change back into the form component.
        editor.fire("change");
        api.close();
      },
    }),
  );
};

export { smallTextBoxConfig, editSmallTextBoxConfig };
